// @flow
import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import { defineMessages, FormattedMessage, intlShape } from 'react-intl';
import Loading from '../../../../elements/Loading/Loading';
import AlertContainer from '../../../alert/container/AlertContainer';
import commonStyle from '../OrderViewCommon.scss';
import OrderBox from '../OrderBox/OrderBox';
import { animateScroll as scroll } from 'react-scroll';
import DeleteModal from '../../../order/Purchase/components/DeleteModal/DeleteModal';
import CancelModal from '../../../order/Purchase/components/CancelModal/CancelModal';

import icons from '../../../../assets/styles/icons.scss';

import type { Order } from '../../../../entities';
import type { HTTPError } from '../../../../error';
import type { LocalValuesState } from '../../../local-values/duck';

type Props = {
  +resetOrdersState: () => void;
  +localValues: LocalValuesState;
  +activePurchases: Order[];
  +isFetchingActives?: boolean;
  +fetchActivePurchases: (resetPagination?: boolean) => void;
  +fetchActivesError?: HTTPError;
  +resetAlertState: () => void;
  +isDeletingOrCancelingOrder: boolean;
  +deleteOrCancelSuccess: boolean;
  +deleteOrCancelOrderError: HTTPError;
  +deleteOrCancelOrder: (orderId: string) => void;
};

type State = {
  openModal: boolean;
  currentOrder: Order;
};

const messages = defineMessages({
  title: {
    id: 'orders.actives.document.title',
  },
  description: {
    id: 'orders.actives.document.description',
  },
});

export default class PurchasesActives extends Component<Props, State> {
  ordersWrapRef: HTMLElement;
  state = {
    openModal: false,
    currentOrder: undefined,
  };

  componentDidUpdate(prevProps: Props): void {
    const { deleteOrCancelOrderError, deleteOrCancelSuccess } = this.props;
    if ((deleteOrCancelOrderError && prevProps.deleteOrCancelOrderError !== deleteOrCancelOrderError) ||
      (deleteOrCancelSuccess && prevProps.deleteOrCancelSuccess !== deleteOrCancelSuccess)) {
      this.setState({ openModal: false });
      scroll.scrollTo(0, {
        duration: 400,
      });
    }
  }

  // Only in browser
  componentDidMount() {
    const { fetchActivePurchases, activePurchases } = this.props;
    if (!activePurchases) {
      fetchActivePurchases();
    }
  }

  componentWillUnmount() {
    const { resetOrdersState, resetAlertState } = this.props;
    resetOrdersState();
    resetAlertState();
  }

  handleOpenModal = (order: Order) => {
    this.setState({ openModal: true, currentOrder: order });
  };

  handleCloseModal = () => {
    this.setState({ openModal: false });
  };


  render() {
    const {
      localValues, activePurchases, isFetchingActives, isDeletingOrCancelingOrder, deleteOrCancelOrder,
    } = this.props;
    const { currentOrder, openModal } = this.state;
    const noResult = (!activePurchases || !activePurchases.length) && !isFetchingActives;
    const orders = activePurchases && activePurchases
      .map((order: Order, n: number) => <OrderBox key={n}
                                                  localValues={localValues}
                                                  handleOpenModal={this.handleOpenModal}
                                                  order={order}/>);
    const productsPurchased = activePurchases &&
      activePurchases.reduce((acc: number, o: Order) => {
        const total = o.invoice && o.invoice.payment ? parseFloat(o.invoice.payment.total) : 0;
        const currentTotal = ['AVAILABLE', 'CREATED'].includes(o.state) ? 0 : total;
        return acc + currentTotal;
      }, 0);

    return (
      <Fragment>
        <Helmet key="helmet">
          <title>{this.context.intl.formatMessage(messages.title)}</title>
          <meta name="description" content={this.context.intl.formatMessage(messages.description)}/>
        </Helmet>
        <section key="mainWrap" className={commonStyle.mainWrap}>
          <section className={commonStyle.rightWrap}>
            <AlertContainer showId="orders"/>
            <header>
              <FormattedMessage id="orders.actives.title">{
                text => <h2><i className={icons.iconBagMedium}/>{text}</h2>
              }</FormattedMessage>
            </header>
            {noResult ?
              (<FormattedMessage id="orders.actives.noResults">{
                txt => <p>{txt}</p>
              }</FormattedMessage>) :
              (!isFetchingActives && typeof productsPurchased === 'number' &&
                <FormattedMessage id="orders.actives.productsPurchased">{
                  txt => <p className={commonStyle.subtitle}>{`${txt}: $ ${productsPurchased.toFixed(2)}`}</p>
                }</FormattedMessage>)}
            <section>
              {orders ? orders : []}
            </section>
            {isFetchingActives && <Loading className={commonStyle.loading}/>}
          </section>
        </section>
        {currentOrder && currentOrder.state === 'CREATED' && <DeleteModal modalVisible={openModal}
                                                                          orderId={currentOrder.id}
                                                                          handleClose={this.handleCloseModal}
                                                                          isDeleting={isDeletingOrCancelingOrder}
                                                                          deleteOrder={deleteOrCancelOrder}/>}
        {currentOrder && currentOrder.state === 'AVAILABLE' && <CancelModal modalVisible={openModal}
                                                                            orderId={currentOrder.id}
                                                                            handleClose={this.handleCloseModal}
                                                                            isCanceling={isDeletingOrCancelingOrder}
                                                                            cancelOrder={deleteOrCancelOrder}/>}
      </Fragment>);
  }
}

PurchasesActives.propTypes = {
  resetOrdersState: PropTypes.func.isRequired,
  localValues: PropTypes.object.isRequired,
  activePurchases: PropTypes.array,
  isFetchingActives: PropTypes.bool,
  fetchActivePurchases: PropTypes.func.isRequired,
  fetchActivesError: PropTypes.object,
  resetAlertState: PropTypes.func.isRequired,
  isDeletingOrCancelingOrder: PropTypes.bool,
  deleteOrCancelSuccess: PropTypes.bool,
  deleteOrCancelOrderError: PropTypes.object,
  deleteOrCancelOrder: PropTypes.func.isRequired,
};

PurchasesActives.contextTypes = {
  intl: intlShape,
};



// WEBPACK FOOTER //
// ./src/modules/orders/components/PurchasesActives/PurchasesActives.jsx